import { useEffect, useState } from 'react';
import type { Site, Page, Component, PageLayout } from '../schemas/validation';
import { useLandingPageContext } from '../contexts/LandingPageContext';
import { getPublicApiBaseUrl } from '../utils/publicApiBaseUrl';
import { parsePageResponse, type NavigationItem } from './parseWebsiteApiData';

export interface LandingPageRouteData {
  loading: boolean;
  /** True when no site has been designated as the Compete Pages landing page. */
  notConfigured: boolean;
  /** True when a landing site exists but it has no page matching this slug. */
  pageNotFound: boolean;
  error: string | null;
  website: Site | null;
  page: Page | null;
  components: Component[];
  navigation: NavigationItem[];
  theme: unknown;
  layout: PageLayout | null;
  showBranding: boolean;
}

const emptyResult = (overrides: Partial<LandingPageRouteData>): LandingPageRouteData => ({
  loading: false,
  notConfigured: false,
  pageNotFound: false,
  error: null,
  website: null,
  page: null,
  components: [],
  navigation: [],
  theme: null,
  layout: null,
  showBranding: false,
  ...overrides,
});

/**
 * Resolves the landing-site page for a given slug on the apex domain.
 * Home-slug data is served from the cached context (single /api/landing-page
 * fetch); non-home slugs fetch /api/landing-page/page/:slug so the page gets
 * its own layout resolution.
 */
export const useLandingPageSite = (slug: string): LandingPageRouteData => {
  const ctx = useLandingPageContext();
  const normalizedSlug = slug.replace(/^\//, '');
  const isHome = normalizedSlug === '' || normalizedSlug === 'home';

  const [pageState, setPageState] = useState<LandingPageRouteData | null>(null);

  useEffect(() => {
    if (isHome || ctx.loading || ctx.notConfigured || ctx.error) {
      setPageState(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    setPageState(
      emptyResult({
        loading: true,
        website: ctx.website,
        navigation: ctx.navigation,
        theme: ctx.theme,
        showBranding: ctx.showBranding,
      })
    );

    const fetchPage = async () => {
      try {
        const apiBaseUrl = getPublicApiBaseUrl();
        const response = await fetch(
          `${apiBaseUrl}/api/landing-page/page/${encodeURIComponent(normalizedSlug)}`,
          { signal: controller.signal }
        );

        if (response.status === 404) {
          if (!cancelled) {
            setPageState(
              emptyResult({
                pageNotFound: true,
                website: ctx.website,
                navigation: ctx.navigation,
                theme: ctx.theme,
                showBranding: ctx.showBranding,
              })
            );
          }
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load page (${response.status})`);
        }

        const body = await response.json();
        const data = body?.success ? body.data : body;
        if (cancelled) return;

        const parsed = parsePageResponse(data);
        setPageState(emptyResult({ ...parsed }));
      } catch (error) {
        if (cancelled || (error as Error).name === 'AbortError') return;
        setPageState(
          emptyResult({
            error: error instanceof Error ? error.message : 'Failed to load page',
            website: ctx.website,
            navigation: ctx.navigation,
            theme: ctx.theme,
            showBranding: ctx.showBranding,
          })
        );
      }
    };

    fetchPage();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    normalizedSlug,
    isHome,
    ctx.loading,
    ctx.notConfigured,
    ctx.error,
    ctx.website,
    ctx.navigation,
    ctx.theme,
    ctx.showBranding,
  ]);

  if (ctx.loading) return emptyResult({ loading: true });
  if (ctx.notConfigured) return emptyResult({ notConfigured: true });
  if (ctx.error) return emptyResult({ error: ctx.error });

  if (isHome) {
    return emptyResult({
      website: ctx.website,
      page: ctx.homePage,
      components: ctx.homeComponents,
      navigation: ctx.navigation,
      theme: ctx.theme,
      layout: ctx.layout,
      showBranding: ctx.showBranding,
    });
  }

  return (
    pageState ??
    emptyResult({
      loading: true,
      website: ctx.website,
      navigation: ctx.navigation,
      theme: ctx.theme,
      showBranding: ctx.showBranding,
    })
  );
};
